"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { Copy, Download } from "lucide-react";
import { NextIntlClientProvider } from "next-intl";
import { ofetch } from "ofetch";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { parse } from "subtitletools";
import * as z from "zod";
import { ErrorElement } from "@/components/ErrorElement";
import { addMessage } from "@/components/Messages";
import { TargetLanguageInput } from "@/components/transcriptions/LanguagesInput";
import { Button } from "@/components/ui/button";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { env } from "@/env";
import { SubtitlesForm } from "@/lib/subtitletools/forms";
import { ResultDialog } from "@/lib/subtitletools/ResultDialog";
import { addRecentSubtitle, recentSubtitlesAtom } from "@/lib/subtitletools/recentSubtitles";
import { TranslationExamples } from "@/lib/subtitletools/TranslationExamples";
import enMessages from "@/messages/en.json";

export const TranslateSubtitlesContent = () => {
  const [translatedSrt, setTranslatedSrt] = useState("");
  const [loadingState, setLoadingState] = useState("INITIAL");
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [recentSubtitles, setRecentSubtitles] = useAtom(recentSubtitlesAtom);

  const formSchema = z.object({
    subtitleText: z.string().min(1),
    targetLanguage: z.string().min(1),
    gptModel: z.string().min(1),
    openAiApiKey: z.string().min(1),
    extraPrompt: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subtitleText: "",
      targetLanguage: "en",
      gptModel: "gpt-4o",
      openAiApiKey: "",
      extraPrompt: "",
    },
  });
  const targetLanguage = form.watch("targetLanguage");

  const handleSubmit = (formData: z.infer<typeof formSchema>) => {
    console.log("formData", formData);
    setLoadingState("LOADING");
    ofetch<{ translatedSrt: string }>(`${env.NEXT_PUBLIC_API_URL}/handleTranslateSubtitles`, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        console.log("response", response);
        setTranslatedSrt(response.translatedSrt);
        setLoadingState("SUCCESS");
        setShowResultDialog(true);

        // Save translated subtitle to recent subtitles
        try {
          const captions = parse(response.translatedSrt);
          const duration =
            captions.length > 0
              ? Math.max(...captions.map((c) => c.end)) - Math.min(...captions.map((c) => c.start))
              : 0;

          setRecentSubtitles(
            addRecentSubtitle(
              response.translatedSrt,
              `Translated to ${targetLanguage} (${captions.length} captions)`,
              captions.length,
              duration
            )
          );
        } catch (error) {
          console.warn("Failed to parse translated subtitle for saving:", error);
        }
      })
      .catch((error) => {
        setLoadingState("ERROR");
        console.error(error);
      });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl mb-8">
          Translate subtitles with ChatGPT
        </h1>
        <div>
          <p>ChatGPT is a reliable and good translator. </p>
          <p>
            We split the subtitle file in order to achieve fast translation, and to handle large
            files which is not possible using ChatGPT directly.
          </p>
        </div>
      </div>
      <NextIntlClientProvider messages={enMessages} locale={"en"} timeZone="Europe/Oslo">
        <div className="mb-6">
          <SubtitlesForm setInputSrt={(newValue) => form.setValue("subtitleText", newValue)} />
        </div>

        <RadixForm.Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div>
              <RadixForm.FormLabel className="block mb-2">Translate to</RadixForm.FormLabel>
              <TargetLanguageInput
                value={targetLanguage}
                setValue={(value) => form.setValue("targetLanguage", value)}
              />
            </div>
            <RadixForm.FormField
              control={form.control}
              name="gptModel"
              render={({ field }) => (
                <RadixForm.FormItem className="space-y-3">
                  <RadixForm.FormLabel>GPT model</RadixForm.FormLabel>
                  <RadixForm.FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      {[
                        {
                          key: "gpt-4o",
                          description: "Fast and good",
                          label: "GPT4o",
                        },
                        {
                          key: "gpt-5",
                          description: "Newest model",
                          label: "GPT5",
                        },
                        {
                          key: "gpt-5-mini",
                          description: "Fast newest model",
                          label: "GPT5 mini",
                        },
                        {
                          key: "gpt-4.1",
                          description: "Probably best",
                          label: "GPT4.1",
                        },
                        {
                          key: "gpt-4o-mini",
                          description: "Faster and decent",
                          label: "GPT4o-mini",
                        },
                        {
                          key: "gpt-4.1-mini",
                          description: "Faster and decent",
                          label: "GPT4.1-mini",
                        },
                      ].map(({ key, description, label }) => (
                        <RadixForm.FormItem
                          key={key}
                          className="flex items-center space-x-3 space-y-0"
                        >
                          <RadixForm.FormControl>
                            <RadioGroupItem value={key} />
                          </RadixForm.FormControl>
                          <RadixForm.FormLabel className="font-normal">{label}</RadixForm.FormLabel>
                          <RadixForm.FormDescription>{description}</RadixForm.FormDescription>
                        </RadixForm.FormItem>
                      ))}
                    </RadioGroup>
                  </RadixForm.FormControl>
                  <RadixForm.FormMessage />
                </RadixForm.FormItem>
              )}
            />
            <RadixForm.FormField
              control={form.control}
              name="openAiApiKey"
              render={({ field }) => {
                return (
                  <RadixForm.FormItem>
                    <RadixForm.FormLabel>OpenAI API key</RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <Input {...field} placeholder="OpenAI API key" />
                    </RadixForm.FormControl>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                );
              }}
            />
            <RadixForm.FormField
              control={form.control}
              name="extraPrompt"
              render={({ field }) => {
                return (
                  <RadixForm.FormItem>
                    <RadixForm.FormLabel>Extra prompt</RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <Textarea {...field} placeholder="Extra prompt" />
                    </RadixForm.FormControl>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                );
              }}
            />
            <div className="flex space-x-2">
              <Button
                type="submit"
                loading={loadingState === "LOADING"}
                disabled={loadingState === "LOADING"}
              >
                Submit
              </Button>
            </div>
            <RadixForm.FormMessage />
          </form>
        </RadixForm.Form>
        <div className="mt-8">
          {loadingState === "LOADING" && (
            <Skeleton className="w-1/2 h-12 flex items-center justify-center text-foreground/40 rounded-xl" />
          )}
          {loadingState === "ERROR" && <ErrorElement />}
          {translatedSrt && loadingState === "SUCCESS" && (
            <div>
              <p>Translated subtitles</p>
              <div className="flex space-x-4 pb-4 pt-2">
                <Button
                  type="button"
                  onClick={() => {
                    if (navigator.clipboard && translatedSrt) {
                      navigator.clipboard.writeText(translatedSrt);
                      addMessage({
                        text: "Copied",
                        type: "SUCCESS",
                      });
                    } else {
                      addMessage({
                        text: "Could not copy",
                        type: "ERROR",
                      });
                    }
                  }}
                >
                  <Copy />
                  &nbsp; Copy
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    const blob = new Blob([translatedSrt], {
                      type: "text/srt",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "translated.srt";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download />
                  &nbsp; Download
                </Button>
              </div>
              <Textarea defaultValue={translatedSrt} rows={12} />
            </div>
          )}
        </div>
        <TranslationExamples />

        {/* Result Dialog */}
        <ResultDialog
          open={showResultDialog}
          onOpenChange={setShowResultDialog}
          title="Translation Complete"
          description={`Successfully translated subtitles to ${targetLanguage}`}
          content={translatedSrt}
          fileName="translated.srt"
        />
      </NextIntlClientProvider>
    </div>
  );
};
